import { memo, useState } from 'react';

import { Box } from '@zillow/constellation-10';

import {
  useRentalManagerUserFreemiumWebApiHandler_details,
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertySummaryWebApiHandler_nextSteps,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import TimeConstants from '../../../../constants/TimeConstants';

import { useAuth } from '../../../../components/AuthProvider';
import ErrorMessagesAlert from '../../../../components/ErrorMessagesAlert';
import SimpleCard from '../../../../components/SimpleCard';
import ApplicationsModal from '../../../modals/ApplicationsModal';
import ProofOfInsuranceModalWrapper from '../../../modals/ProofOfInsuranceModal';

import {
  emitClickNextStepsRecommendationsEvent,
  emitClickUpgradeToPremiumFromRecActionsEvent,
} from '../../../../analytics/events/nextStepsRecommendations';
import ConnectedModuleCard from '../../../ConnectedModuleCard';
import PremiumPaymentGateway from '../../../PaymentGateway/PremiumPaymentGateway';
import Section from './Section';

import type { TileType } from '../../../../model/NextSteps';

const TILE_ID = {
  ENABLE_APPLICATIONS: 'enableApplications',
  UPGRADE_TO_PREMIUM: 'upgradeToPremium',
  PROOF_OF_INSURANCE: 'proofOfInsurance',
} as const;

const MOCK_SECTION = {
  id: 'loadingSection',
  title: '',
  tiles: [
    {
      id: 'loading',
      title: ' ',
      icon: ' ',
      url: '',
    },
  ],
};

type Props = {
  listingAlias: string;
};

const NextStepsModule = ({ listingAlias }: Props) => {
  const { user } = useAuth();
  const [showProofOfInsuranceModal, setShowProofOfInsuranceModal] = useState(false);
  const [showEnableApplicationsModal, setShowEnableApplicationsModal] = useState(false);
  const [showUpgradeToPremiumGateway, setShowUpgradeToPremiumGateway] = useState(false);

  const { data: overviewData } = useRentalManagerUserPropertyDetailsWebApiHandler_overview(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );
  const { data: freemiumData } = useRentalManagerUserFreemiumWebApiHandler_details(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );

  const { data, isPending, isFetching, error } = useRentalManagerUserPropertySummaryWebApiHandler_nextSteps(
    { listingAlias },
    { enabled: Boolean(listingAlias), staleTime: TimeConstants.SECONDS_10 },
  );

  const { sections: sectionList = [{ ...MOCK_SECTION, title: 'Next steps' }, MOCK_SECTION] } = data || {};
  const isNotLoadingAndSectionListEmpty = isFetching === false && isPending === false && sectionList.length === 0;

  const handleTileClick = ({ sectionTitle, id, url }: { sectionTitle: string; id: string; url: string }) => {
    const triggerObject = sectionTitle.toLowerCase().replace(/\s/g, '_');
    const triggerSource = id
      .split(/(?=[A-Z])/)
      .map((substring) => substring.toLowerCase())
      .join('_');

    if (user) {
      emitClickNextStepsRecommendationsEvent({
        user,
        currentProperty: { listingAlias },
        triggerSource,
        triggerObject,
      });
    }

    if (!url) {
      if (id === TILE_ID.PROOF_OF_INSURANCE) {
        setShowProofOfInsuranceModal(true);
      }
      if (id === TILE_ID.ENABLE_APPLICATIONS) {
        setShowEnableApplicationsModal(true);
      }
      if (id === TILE_ID.UPGRADE_TO_PREMIUM) {
        if (user) {
          emitClickUpgradeToPremiumFromRecActionsEvent({
            user,
            currentProperty: { listingAlias },
          });
        }
        setShowUpgradeToPremiumGateway(true);
      }
      return;
    }

    window.location.href = url;
  };

  const renderEmptySectionList = () => {
    return (
      <Box>
        <SimpleCard
          bodyHeading={'Nice work!'}
          body={'Your property looks good. Check back here for future actions you can take to manage your rental.'}
          withChevron={false}
        />
      </Box>
    );
  };

  const renderSectionList = ({ title, tiles }: { title: string; tiles: Array<TileType> }, index: number) => {
    const titleFontType = index === 0 ? 'body-bold' : 'body-sm-bold';
    const titleFontColor = index === 0 ? 'text.neutral' : 'text.subtle';
    const titleMarginBottom = index === 0 ? '400' : '200';

    return (
      <Section
        title={title}
        tileList={tiles}
        titleFontColor={titleFontColor}
        titleFontType={titleFontType}
        titleMarginBottom={titleMarginBottom}
        tileFontType="body-xs-bold"
        tileSubtitleFontType="body-xs"
        onTileClick={handleTileClick}
        isLoading={isPending}
        key={`Section-${index}`}
      />
    );
  };

  return (
    <>
      <div data-testid="NextStepsModule">
        <ConnectedModuleCard
          title={isNotLoadingAndSectionListEmpty ? 'Next steps' : undefined}
          ctaFontColor="text.subtle"
          ctaFontType="body-sm"
          enableHover={false}
          justifyHeadingContent={'center'}
          css={{
            borderRadius: 'Card.Root',
          }}
        >
          {error ? (
            <ErrorMessagesAlert
              data-testid="NextStepsModuleError"
              errorMessages={
                DatarouterError.getErrorDetails(error)?.partialMessages || [
                  {
                    text:
                      DatarouterError.getErrorDetails(error)?.message ||
                      'Unable to load next steps. Please refresh the page to try again.',
                  },
                ]
              }
            />
          ) : (
            <>{isNotLoadingAndSectionListEmpty ? renderEmptySectionList() : sectionList.map(renderSectionList)}</>
          )}
        </ConnectedModuleCard>
      </div>
      {showProofOfInsuranceModal && (
        <ProofOfInsuranceModalWrapper listingAlias={listingAlias} onClose={() => setShowProofOfInsuranceModal(false)} />
      )}
      {showEnableApplicationsModal && (
        <ApplicationsModal listingAlias={listingAlias} onClose={() => setShowEnableApplicationsModal(false)} />
      )}
      {showUpgradeToPremiumGateway && (
        <PremiumPaymentGateway
          listingAlias={listingAlias}
          isActive={overviewData?.isActive ?? false}
          freemiumData={freemiumData}
          isPremiumMidfunnelPayment={true}
          displayContext="NEXT_STEP"
          isInBuildingGroup={overviewData?.freemium?.isInBuildingGroup ?? false}
          onClose={() => setShowUpgradeToPremiumGateway(false)}
        />
      )}
    </>
  );
};

export default memo(NextStepsModule);
